/**
 * moon_view — a planet's moons (natural objects, 2026-10-07; catalog PL-18 … PL-23, SY-03 … SY-06).
 *
 * A moon is a small world of its own: the planet material over a pooled face with its kind's surface (world_look.
 * moonKindSpec), on a circle about its planet on the WALL clock (natural_orbit.moonOffsetAt — every client sees it in
 * the same place), one face turned to its planet, lit from ITS OWN direction to the star. It is registered with the
 * sector (it hides the sun's disc, it is a body on the charts) and it is solid.
 *
 * THE MOON'S SEAT. A moon of a far planet is drawn at the same 100 km as its planet, where one depth code is ~12 km:
 * the depth buffer cannot say which is in front. So each frame the moon is moved ALONG ITS OWN VIEW RAY and scaled with
 * the move (the far-body trick again — the same direction, the same angular size, the same picture) until the buffer
 * can: in front of its planet's limb plane it is brought nearer than the planet's nearest point by 8 codes; behind it,
 * 4 codes beyond the limb and never into the far plane's last codes, where the sky is drawn over an opaque
 * (world_look.moonSeatFactor, pinned by verify:natural-worlds; pictured by the bed with the sector's own planes). The
 * limb plane decides it exactly: a point on the camera's side of it is never behind the visible cap, and a point beyond
 * it that falls inside the disc is hidden by the planet.
 */
import * as THREE from 'three/webgpu';
import { planetMaterial, buildAtmosphereShell, type PlanetLook } from '../planet_look.js';
import { WORLD_PLANET_POOL } from '../../space_sector.js';
import { ASSET_STORE_BASE } from '../../../config.js';
import { moonOffsetAt, type V3 } from './natural_orbit.js';
import type { MoonMakeup } from './natural_types.js';
import type { NaturalEnv, PlanetHandle, ColliderGroup } from './natural_api.js';
import { moonKindSpec, moonSeatFactor, moonInFront, mix01, FAR_ANCHOR_M, type MoonKindSpec, type KindOpts } from './world_look.js';
import { faceByName, moonFaceKey } from './face_catalog.js';
import { buildTail, anyPerp, type TailHandle } from './world_layers.js';
import { deferDisposeTree } from '../../defer_dispose.js';

// ── the faces, small (a moon is a few dozen pixels: a 1024-wide copy of its face, kept for the session) ──
/** `localStorage atlas:planet-faces = 'off'` → the five pooled faces alone (next sector entry). */
export function catalogFacesOn(): boolean {
  try { return localStorage.getItem('atlas:planet-faces') !== 'off'; } catch { return true; }
}
const faceCache = new Map<string, Promise<THREE.Texture | null>>();
/** What the small-face loader has done this session (`__natural().worlds` shows it; the world probe reads it). */
export const smallFaceState = { loaded: [] as string[], fellBack: [] as string[], failed: [] as string[] };
const catalogMissing = new Set<string>();
/** True once the catalog face `faceKey` has failed to load this session: its wearers show their pooled face (PlanetLook.wearPooled). */
export const smallFaceFellBack = (faceKey: string | null): boolean => !!faceKey && catalogMissing.has(faceKey);
async function decodeSmall(key: string): Promise<THREE.Texture | null> {
  const res = await fetch(`${ASSET_STORE_BASE}/${key}`);
  if (!res.ok) return null;
  const bmp = await createImageBitmap(await res.blob(), { resizeWidth: 1024, resizeHeight: 512, resizeQuality: 'high', imageOrientation: 'flipY' });
  const t = new THREE.Texture(bmp as unknown as HTMLImageElement);
  t.flipY = false; // flipped at decode: an ImageBitmap's own flip differs between the backends
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  t.needsUpdate = true;
  t.userData.shared = true;
  return t;
}
/**
 * A face at moon size (1024 × 512), decoded off the main thread, shared for the session (`userData.shared`): the catalog
 * face `faceKey` (face_catalog.ts — its 2048 × 1024 copy) when one is named, else the pooled face. THE FALLBACK: a catalog
 * face that does not load (a missing object, a decode error) hands back the POOLED face — a moon is never left bare for it.
 */
export function smallFace(face: number, faceKey: string | null = null): Promise<THREE.Texture | null> {
  const row = faceKey ? faceByName(faceKey) : undefined;
  if (row) {
    // the catalog face is asked for ONCE a session; a miss resolves null and each wearer takes ITS OWN pooled face
    // (two kinds may share a catalog face and not a pooled one)
    const cid = `face:${row.name}`;
    let c = faceCache.get(cid);
    if (!c) {
      c = (async (): Promise<THREE.Texture | null> => {
        try { const t = await decodeSmall(moonFaceKey(row)); if (t) { smallFaceState.loaded.push(row.name); return t; } } catch { /* the pooled face */ }
        catalogMissing.add(row.name);
        smallFaceState.fellBack.push(row.name);
        return null;
      })();
      faceCache.set(cid, c);
    }
    return c.then((t) => t ?? smallFace(face, null));
  }
  const id = `pool:${face}`;
  let p = faceCache.get(id);
  if (p) return p;
  const spec = WORLD_PLANET_POOL[face] ?? WORLD_PLANET_POOL[3];
  p = (async (): Promise<THREE.Texture | null> => {
    try { const t = await decodeSmall(spec.tex); if (t) smallFaceState.loaded.push(id); else smallFaceState.failed.push(id); return t; } catch { smallFaceState.failed.push(id); return null; }
  })();
  faceCache.set(id, p);
  return p;
}

/** A lumpy body: a UV sphere pushed in and out by a few smooth waves of its own direction (no seam: both sides of the u = 0 cut move alike). */
export function lumpGeometry(radius: number, seed: number, lumps: number, stretch: number, ws = 28, hs = 20, profile?: (sinLat: number) => number): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(1, ws, hs);
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  const waves: Array<{ w: THREE.Vector3; ph: number; a: number }> = [];
  for (let k = 0; k < 7; k++) {
    const u = (s: string): number => mix01(`lump:${seed}:${k}:${s}`);
    waves.push({ w: new THREE.Vector3(u('x') - 0.5, u('y') - 0.5, u('z') - 0.5).normalize().multiplyScalar(1.4 + k * 0.75), ph: u('p') * 6.283, a: 1 / (1 + k * 0.6) });
  }
  const norm = waves.reduce((s, w) => s + w.a, 0);
  // (`profile`: a body of revolution under the lumps — a top-shaped rubble pile's two cones and ridge)
  const h = (d: THREE.Vector3): number => { let s = 0; for (const w of waves) s += w.a * Math.sin(d.dot(w.w) + w.ph); return (1 + (lumps * s) / norm * 1.6) * (profile ? profile(d.y) : 1); };
  const d = new THREE.Vector3(), t = new THREE.Vector3(), b = new THREE.Vector3(), p0 = new THREE.Vector3(), p1 = new THREE.Vector3(), p2 = new THREE.Vector3();
  const place = (dir: THREE.Vector3, out: THREE.Vector3): THREE.Vector3 => out.copy(dir).multiplyScalar(h(dir) * radius).multiply(t.set(stretch, 1 / Math.sqrt(stretch), 1 / Math.sqrt(stretch)));
  const nrm = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    d.fromBufferAttribute(pos, i).normalize();
    // the normal from the shape itself (two neighbours on the sphere), so the seam's twin vertices agree
    const a = anyPerp(d, new THREE.Vector3()), c = b.crossVectors(d, a).normalize();
    place(d, p0);
    place(p1.copy(d).addScaledVector(a, 0.02).normalize(), p1);
    place(p2.copy(d).addScaledVector(c, 0.02).normalize(), p2);
    const n = p1.sub(p0).cross(p2.sub(p0)).normalize();
    if (n.dot(d) < 0) n.negate();
    pos.setXYZ(i, p0.x, p0.y, p0.z);
    nrm[i * 3] = n.x; nrm[i * 3 + 1] = n.y; nrm[i * 3 + 2] = n.z;
  }
  g.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  pos.needsUpdate = true;
  g.computeBoundingSphere();
  return g;
}

interface Moon {
  m: MoonMakeup; spec: MoonKindSpec;
  group: THREE.Group; seat: THREE.Group; mesh: THREE.Mesh; look: PlanetLook;
  abs: THREE.Vector3; collider: ColliderGroup | null;
  plumes: Array<{ tail: TailHandle; dir: THREE.Vector3 }>;
  /** The haze shell's own sun (a moon's sun turns as it orbits). */
  shellSun: { value: THREE.Vector3 } | null;
  release: Array<() => void>;
  front: boolean; factor: number;
}
export interface MoonSet {
  /** Per sector frame: orbits, the turn to the planet, the sun, the seat. */
  frame(nowS: number): void;
  state(): Array<Record<string, unknown>>;
  /** The first volcanic moon's orbit radius (m) — the plasma torus rides there. */
  volcanicOrbitM: number;
  dispose(): void;
}

/** Hang a planet's moons (children of the ROOT on their own far-body anchors, never of the planet's group: each stands on its own view ray). */
export function buildMoons(env: NaturalEnv, planet: PlanetHandle, moons: ReadonlyArray<MoonMakeup>, key: string, specs?: ReadonlyArray<MoonKindSpec | null>, opt: KindOpts = {}): MoonSet {
  const root = env.root();
  const list: Moon[] = [];
  const off: V3 = [0, 0, 0];
  const sun = new THREE.Vector3();
  if (root) for (const [i, m] of moons.entries()) {
    const spec = specs?.[i] ?? moonKindSpec(m, opt); // (a caller's own look: a dwarf planet's large moon)
    env.sunAt(planet.abs, sun);
    const look = planetMaterial({ rim: spec.rim, atmosphere: spec.atmosphere, ocean: false, tint: spec.tint, surface: spec.surface, sunDir: sun });
    const geo = spec.lumps > 0 ? lumpGeometry(m.radiusM, m.seed, spec.lumps, spec.stretch) : new THREE.SphereGeometry(m.radiusM, env.level === 'low' ? 32 : 48, env.level === 'low' ? 20 : 32);
    const mesh = new THREE.Mesh(geo, look.material);
    mesh.name = `moon:${planet.name}:${i}:${m.kind}`;
    mesh.userData.planetLook = look;
    void smallFace(spec.face, spec.faceKey).then((t) => { if (!t || env.root() !== root) return; if (smallFaceFellBack(spec.faceKey)) look.wearPooled(); look.setMap(t); });
    const group = new THREE.Group(), seat = new THREE.Group();
    group.name = `moon-anchor:${planet.name}:${i}`;
    seat.add(mesh);
    group.add(seat);
    root.add(group);
    const abs = new THREE.Vector3().copy(planet.abs);
    env.anchor(group, abs);
    const release: Array<() => void> = [() => env.unanchor(group)];
    release.push(env.addBody({ mesh, abs, radius: m.radiusM, name: `${planet.name} ${'abcdef'[i] ?? i}`, spin: 0 }));
    // a haze shell (PL-22): the planets' own builder, standing behind ITS moon
    let shellSun: Moon['shellSun'] = null;
    if (spec.shell && env.flags.atmosphere) {
      try {
        const sh = buildAtmosphereShell({ ...spec.shell.extra, radius: m.radiusM, hue: spec.shell.hue, sunDir: look.sunDir.value }).mesh;
        shellSun = (sh.userData.sunDir as Moon['shellSun']) ?? null;
        seat.add(sh);
      } catch { /* the moon without its haze */ }
    }
    // plumes (PL-19, PL-21): strips that face the camera, children of the moon's seat (they turn with the moon by hand)
    const plumes: Moon['plumes'] = [];
    if (spec.plumes && env.particleCap > 0) {
      for (let k = 0; k < spec.plumes.count; k++) {
        const u = (s: string): number => mix01(`${key}:plume:${m.seed}:${k}:${s}`);
        const tail = buildTail({ profile: spec.plumes.polar ? 'jet' : 'umbrella', color: spec.plumes.color, seed: m.seed + k, g: spec.plumes.polar ? 0.72 : 0.45, segs: 12, across: spec.plumes.polar ? 4 : 10, bodyR: m.radiusM });
        // a polar moon's jets fan out round its south pole; a volcano's plume stands anywhere
        const dir = spec.plumes.polar
          ? new THREE.Vector3(Math.sin(k * 2.1) * 0.22, -1, Math.cos(k * 2.1) * 0.22).normalize()
          : new THREE.Vector3(u('x') - 0.5, (u('y') - 0.5) * 0.8, u('z') - 0.5).normalize();
        for (const pm of tail.meshes) seat.add(pm);
        plumes.push({ tail, dir });
      }
    }
    let collider: ColliderGroup | null = null;
    if (m.radiusM >= 600) {
      collider = { x: abs.x, y: abs.y, z: abs.z, reach: m.radiusM * 1.2 + 900, list: [{ x: abs.x, y: abs.y, z: abs.z, r: m.radiusM * 0.97, halfH: 0 }] };
      release.push(env.addCollider(collider));
    }
    list.push({ m, spec, group, seat, mesh, look, abs, collider, plumes, shellSun, release, front: true, factor: 1 });
  }
  const camAbs = new THREE.Vector3(), v = new THREE.Vector3(), pv = new THREE.Vector3(), q = new THREE.Quaternion(), dirW = new THREE.Vector3();
  const frame = (nowS: number): void => {
    const ship = env.shipAbs(), origin = env.origin(), cam = env.camera();
    for (const mo of list) {
      const m = mo.m;
      moonOffsetAt(m, nowS, off);
      mo.abs.set(planet.abs.x + off[0], planet.abs.y + off[1], planet.abs.z + off[2]);
      if (mo.collider) { const c = mo.collider; c.x = mo.abs.x; c.y = mo.abs.y; c.z = mo.abs.z; c.list[0].x = c.x; c.list[0].y = c.y; c.list[0].z = c.z; }
      // one face to its planet (tidally locked): the turn follows the orbit; an irregular tumbles instead
      const th = m.phase0 + m.sense * (nowS / m.periodS) * Math.PI * 2;
      mo.mesh.rotation.y = mo.spec.tumble > 0 ? (nowS * mo.spec.tumble) % (Math.PI * 2) : -th;
      // its own sun
      env.sunAt(mo.abs, sun);
      mo.look.sunDir.value.copy(sun);
      if (mo.shellSun) mo.shellSun.value.copy(sun);
      for (const p of mo.plumes) {
        dirW.copy(p.dir).applyQuaternion(q.setFromEuler(mo.mesh.rotation));
        p.tail.axis.copy(dirW);
        p.tail.sun.copy(sun);
        const h = m.radiusM * (mo.spec.plumes?.heightR ?? 0.3);
        const polar = mo.spec.plumes?.polar === true;
        // a jet is a narrow fan; a volcano's umbrella is the parabola of everything thrown at one speed — 1.6 heights wide at the ground
        p.tail.set(m.radiusM * 0.97, h, 0, polar ? m.radiusM * 0.05 : h * 1.6, polar ? h * 0.3 : h * 1.6, mo.spec.plumes?.gain ?? 0.4);
      }
      // THE MOON'S SEAT — along its own view ray, by depth codes (the header)
      let f = 1, front = true;
      if (cam) {
        camAbs.set(cam.pos.x + origin.x, cam.pos.y + origin.y, cam.pos.z + origin.z);
        const dm = Math.hypot(mo.abs.x - ship.x, mo.abs.y - ship.y, mo.abs.z - ship.z);
        const km = dm > FAR_ANCHOR_M ? FAR_ANCHOR_M / dm : 1;
        // where the sector will draw the moon this frame (root frame), and the same for its planet
        v.set(ship.x - origin.x + (mo.abs.x - ship.x) * km, ship.y - origin.y + (mo.abs.y - ship.y) * km, ship.z - origin.z + (mo.abs.z - ship.z) * km).sub(cam.pos);
        const dp = Math.hypot(planet.abs.x - ship.x, planet.abs.y - ship.y, planet.abs.z - ship.z);
        const kp = dp > FAR_ANCHOR_M ? FAR_ANCHOR_M / dp : 1;
        pv.set(ship.x - origin.x + (planet.abs.x - ship.x) * kp, ship.y - origin.y + (planet.abs.y - ship.y) * kp, ship.z - origin.z + (planet.abs.z - ship.z) * kp).sub(cam.pos);
        front = moonInFront(off, [camAbs.x - planet.abs.x, camAbs.y - planet.abs.y, camAbs.z - planet.abs.z], planet.radius);
        f = moonSeatFactor(front, pv.length(), planet.radius * kp, v.length(), m.radiusM * km);
        if (!Number.isFinite(f) || f <= 0) f = 1;
        mo.seat.position.copy(v).multiplyScalar((f - 1) / km);
        mo.seat.scale.setScalar(f);
      } else { mo.seat.position.set(0, 0, 0); mo.seat.scale.setScalar(1); }
      mo.front = front; mo.factor = f;
    }
  };
  return {
    frame,
    volcanicOrbitM: moons.find((m) => m.kind === 'volcanic')?.orbitM ?? 0,
    state: () => list.map((mo) => ({ kind: mo.m.kind, faceKey: mo.spec.faceKey, shepherd: !!mo.m.shepherd, radiusM: Math.round(mo.m.radiusM), orbitM: Math.round(mo.m.orbitM), front: mo.front, seat: +mo.factor.toFixed(4), plumes: mo.plumes.length, abs: [Math.round(mo.abs.x), Math.round(mo.abs.y), Math.round(mo.abs.z)] })),
    dispose: () => { for (const mo of list) { for (const r of mo.release) { try { r(); } catch { /* the sector is going */ } } deferDisposeTree(mo.group); } list.length = 0; },
  };
}
